"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from "clsx";
import { useId, useEffect } from "react";
import { Button } from "./Button.js";
const shouldLogTicketSelectorDebug = process.env.NODE_ENV !== "production";
const sanitizeConstraint = (value) => {
    if (typeof value !== "number") {
        return undefined;
    }
    if (!Number.isFinite(value)) {
        return undefined;
    }
    const coerced = Math.floor(value);
    return coerced < 0 ? 0 : coerced;
};
export const resolveTicketQuantityLimitState = (limits) => {
    const minPerOrder = sanitizeConstraint(limits?.minPerOrder);
    const maxPerOrder = sanitizeConstraint(limits?.maxPerOrder);
    const perUserLimit = sanitizeConstraint(limits?.perUserLimit);
    const perUserRemaining = sanitizeConstraint(limits?.perUserRemaining);
    const remaining = sanitizeConstraint(limits?.remaining);
    const minimum = Math.max(minPerOrder && minPerOrder > 0 ? minPerOrder : 1, 1);
    const candidates = [maxPerOrder, perUserLimit, perUserRemaining, remaining]
        .filter((value) => typeof value === "number")
        .filter((value) => value >= 0);
    const maximum = candidates.length > 0 ? Math.min(...candidates) : undefined;
    let selectable = true;
    if (typeof remaining === "number" && remaining <= 0) {
        selectable = false;
    }
    if (typeof maximum === "number") {
        if (maximum <= 0) {
            selectable = false;
        }
        else if (minPerOrder && minPerOrder > 0 && maximum < minPerOrder) {
            selectable = false;
        }
    }
    return { minimum, maximum, selectable };
};
const buildLimitDescription = (limits, state, currentValue) => {
    const parts = [];
    const minPerOrder = sanitizeConstraint(limits?.minPerOrder);
    if (minPerOrder && minPerOrder > 1) {
        parts.push(`Mínimo ${minPerOrder}`);
    }
    const maxPerOrder = sanitizeConstraint(limits?.maxPerOrder);
    const perUserLimit = sanitizeConstraint(limits?.perUserLimit);
    const perUserRemaining = sanitizeConstraint(limits?.perUserRemaining);
    const remaining = sanitizeConstraint(limits?.remaining);
    // Only a computed leftover of 0 proves this user maxed out. remaining === 0
    // with a configured per-user cap is NOT proof: it also happens on global
    // sellout and when stock could not be read.
    const userHitPersonalLimit = typeof perUserRemaining === "number" && perUserRemaining === 0;
    // Stock could not be verified: fail-closed, but never claim "sold out".
    if (limits?.stockUnknown && !userHitPersonalLimit) {
        if (limits.stockCheckFailed) {
            return "No pudimos verificar la disponibilidad. Tocá para reintentar.";
        }
        return "Verificando disponibilidad…";
    }
    if (userHitPersonalLimit) {
        return "Límite máximo alcanzado";
    }
    if (typeof remaining === "number" && remaining === 0) {
        return "Agotado";
    }
    // Determine which limit is actually capping the user
    const limitingFactors = [];
    if (maxPerOrder)
        limitingFactors.push(maxPerOrder);
    if (perUserLimit)
        limitingFactors.push(perUserLimit);
    if (perUserRemaining)
        limitingFactors.push(perUserRemaining);
    if (typeof remaining === "number")
        limitingFactors.push(remaining);
    const effectiveMaximum = limitingFactors.length > 0 ? Math.min(...limitingFactors) : undefined;
    // Show max per order only if it's the limiting factor and user has reached it
    if (maxPerOrder && maxPerOrder > 0 && effectiveMaximum === maxPerOrder && currentValue >= maxPerOrder) {
        parts.push(`Máx ${maxPerOrder} por orden`);
    }
    // Show max per user only if it's the limiting factor and user has reached it
    if (perUserLimit && perUserLimit > 0 && effectiveMaximum === perUserLimit && currentValue >= perUserLimit) {
        parts.push(`Máx ${perUserLimit} por usuario`);
    }
    // Show remaining only if it's the limiting factor and low stock
    if (typeof remaining === "number" && remaining > 0) {
        if (effectiveMaximum === remaining && currentValue >= remaining) {
            parts.push(`${remaining} disponible${remaining > 1 ? 's' : ''}`);
        }
    }
    // Only show "No disponible" if truly not selectable AND not due to personal limit
    if (!state.selectable && !userHitPersonalLimit) {
        if (parts.length > 0) {
            return `No disponible • ${parts.join(" • ")}`;
        }
        return "No disponible";
    }
    if (parts.length === 0) {
        return null;
    }
    return parts.join(" • ");
};
export function TicketQuantitySelector({ id, name, description, price, value, onChange, limits, disabled = false, className, imageUrl, comboLabel, validToLabel, onStockRetry, }) {
    const generatedId = useId();
    const controlId = id ?? generatedId;
    const state = resolveTicketQuantityLimitState(limits);
    // Debug logging
    if (shouldLogTicketSelectorDebug && limits && (limits.perUserLimit || limits.maxPerOrder)) {
        console.log('[TicketSelector] Limits:', {
            name,
            limits,
            state,
            currentValue: value,
            maximum: state.maximum,
        });
    }
    const minPerOrder = sanitizeConstraint(limits?.minPerOrder);
    const minimumToSelect = state.minimum;
    const maximum = state.maximum ?? Number.POSITIVE_INFINITY;
    // Ensure value doesn't exceed maximum on mount or if limits change
    useEffect(() => {
        if (state.maximum !== undefined && value > state.maximum) {
            if (shouldLogTicketSelectorDebug) {
                console.log('[TicketSelector] Value exceeds maximum, correcting:', { value, maximum: state.maximum });
            }
            onChange(state.maximum);
        }
    }, [state.maximum, value, onChange]);
    // CRITICAL: Disable increment button when at or above maximum
    // This prevents any possibility of exceeding the limit
    const canIncrement = !disabled &&
        state.selectable &&
        state.maximum !== undefined &&
        value < state.maximum;
    // If no maximum is set, allow incrementing (but still respect other constraints)
    const canIncrementUnlimited = !disabled &&
        state.selectable &&
        state.maximum === undefined &&
        (value === 0 ? minimumToSelect <= maximum : value < maximum);
    const finalCanIncrement = canIncrement || canIncrementUnlimited;
    const canDecrement = !disabled && value > 0;
    // Debug logging - always log to help diagnose issues
    if (shouldLogTicketSelectorDebug && limits) {
        console.log('[TicketSelector] Limits:', {
            name,
            limits,
            state,
            currentValue: value,
            maximum: state.maximum,
            canIncrement: finalCanIncrement,
        });
    }
    const handleIncrease = () => {
        if (!finalCanIncrement) {
            return;
        }
        const nextValue = value === 0 ? minimumToSelect : value + 1;
        // CRITICAL: Enforce maximum limit - never allow exceeding
        const clampedValue = state.maximum !== undefined
            ? Math.min(nextValue, state.maximum)
            : nextValue;
        // Ensure we don't go below minimum if we're starting from 0
        const finalValue = value === 0 && clampedValue < minimumToSelect
            ? minimumToSelect
            : clampedValue;
        // Double-check: never exceed maximum
        const safeValue = state.maximum !== undefined
            ? Math.min(finalValue, state.maximum)
            : finalValue;
        if (safeValue !== value && safeValue <= maximum) {
            onChange(safeValue);
        }
    };
    const handleDecrease = () => {
        if (!canDecrement) {
            return;
        }
        if (minPerOrder && minPerOrder > 0 && value <= minPerOrder) {
            if (value !== 0) {
                onChange(0);
            }
            return;
        }
        const nextValue = Math.max(0, value - 1);
        if (nextValue !== value) {
            onChange(nextValue);
        }
    };
    const limitDescription = buildLimitDescription(limits ?? {}, state, value);
    /** True when the ticket is FREE (price string contains "GRATIS" or "FREE"). */
    const isFreeTicket = price?.toUpperCase() === "GRATIS" || price?.toUpperCase() === "FREE";
    return (_jsxs("section", { "aria-labelledby": `${controlId}-label`, className: clsx("rounded-xl border border-white/10 bg-white/[0.06] p-4 transition-colors hover:bg-white/10", disabled ? "opacity-50 pointer-events-none" : undefined, className), children: [_jsxs("div", { className: "flex items-center justify-between gap-4", children: [_jsxs("div", { className: "flex gap-4 flex-1 min-w-0", children: [imageUrl && (_jsx("div", { className: "flex-shrink-0", children: _jsx("img", { src: imageUrl, alt: name, className: "w-20 h-20 md:w-24 md:h-24 rounded-lg object-cover border border-white/10", loading: "eager", decoding: "async", onError: (e) => {
                                        console.warn(`[TicketSelector] Failed to load image for "${name}":`, imageUrl);
                                        const target = e.currentTarget;
                                        target.style.display = 'none';
                                    } }) })), _jsxs("div", { className: "space-y-0.5 flex-1 min-w-0", children: [_jsxs("div", { id: `${controlId}-label`, className: "flex items-center gap-2", children: [_jsx("span", { className: "text-[0.95rem] font-semibold text-white", children: name }), comboLabel ? (_jsx("span", { className: "text-[0.7rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md", style: { background: "rgba(255,187,249,0.3)", color: "var(--gruv-pink)" }, "aria-label": `Combo: ${comboLabel}`, children: comboLabel })) : null] }), validToLabel ? (typeof validToLabel === "string" ? (_jsx("p", { className: "text-[0.8rem] text-white/50", children: validToLabel })) : (validToLabel)) : null, description ? (_jsx("p", { className: "text-[0.8rem] leading-relaxed text-white/50", children: description })) : null, limitDescription ? (limits?.stockUnknown && limits?.stockCheckFailed && onStockRetry ? (_jsx("button", { type: "button", className: "text-[0.75rem] text-white/40 underline-offset-2 hover:underline text-left", onClick: onStockRetry, children: limitDescription })) : (_jsx("p", { className: "text-[0.75rem] text-white/40", children: limitDescription }))) : null] })] }), _jsx("div", { className: "flex-shrink-0 text-right", children: isFreeTicket ? (_jsx("span", { className: "inline-block text-[0.8rem] font-bold uppercase tracking-wide px-3 py-1 rounded-lg", style: { background: "rgba(34,197,94,0.2)", color: "#22c55e" }, children: price })) : price ? (_jsx("span", { className: "text-base font-bold text-white", children: price })) : null })] }), _jsxs("div", { className: "mt-3 flex items-center justify-end gap-3", children: [_jsx(Button, { type: "button", variant: "secondary", size: "sm", onClick: handleDecrease, disabled: !canDecrement, "aria-label": `Remove ${name}`, children: "\u2212" }), _jsx("output", { id: `${controlId}-value`, className: "min-w-[2ch] text-center font-mono text-lg", "aria-live": "polite", "aria-atomic": "true", children: value }), _jsx(Button, { type: "button", variant: "secondary", size: "sm", onClick: handleIncrease, disabled: !finalCanIncrement, "aria-label": `Add ${name}`, children: "+" })] })] }));
}
